import { cva, type VariantProps } from 'class-variance-authority';
import { useId, type CSSProperties } from 'react';

import { cn } from '@/utils/helpers/cn';

import {
  ARCUS_MARK_RADIUS,
  ARCUS_RING_WIDTH,
  ARCUS_RINGS,
  describeArcShape,
  resolveSweep,
  type ArcusCaps,
  type ArcusRing,
  type ArcusRingTone,
  type ArcusSweep,
  type ArcusSweepName,
} from './arcus-mark-rings';

/**
 * O símbolo do Arcus, opcionalmente em varredura.
 *
 * A marca é feita de anéis concêntricos incompletos ao redor de um ponto — cada
 * um uma cauda que ganha opacidade até virar cor chapada. Animar é só girar cada
 * anel em torno do centro: o desenho parado já é um quadro dessa varredura, e é
 * por isso que a animação não inventa nada sobre a marca.
 *
 * A geometria mora em `arcus-mark-rings.ts`, medida do arquivo oficial.
 *
 * ## Diferenças conscientes em relação a `public/arcus-simbolo.svg`
 *
 * 1. **Cor por token.** O arquivo usa `#367FC2`/`#FBBC3A`; aqui saem de
 *    `--brand-400`/`--accent-400` (`#347DC0`/`#FBBA00`), como manda a regra de
 *    zero hex em componente. A diferença é de um dígito e imperceptível.
 * 2. **Símbolo centralizado na placa.** No arquivo o símbolo está 11px à direita
 *    e 29px acima do centro do quadro — deslocamento que a rotação evidenciaria
 *    como um wobble. Aqui ele é concêntrico com a placa.
 * 3. **Sem o vão de 15° no anel azul.** O original quebra o arco às 9h por causa
 *    de como as máscaras do Figma se encontram; a reconstrução fecha o arco.
 *
 * ## Tamanho
 *
 * Vem do `className` (`size-12`, `w-full`), não de uma prop: o SVG ocupa 100%
 * da caixa e mantém a proporção sozinho.
 */

const arcusMarkVariants = cva('block', {
  variants: {
    /** Sobre qual superfície a marca vai. Decide a cor da lâmpada. */
    tone: {
      'on-dark': '',
      'on-light': '',
    },
    /** Placa arredondada por trás, como no ícone do app. */
    plate: {
      none: '',
      navy: '',
    },
  },
  defaultVariants: { tone: 'on-dark', plate: 'none' },
});

const RING_FILL: Record<ArcusRingTone, Record<'on-dark' | 'on-light', string>> = {
  // Sobre fundo claro a lâmpada branca sumiria: vira o navy da própria marca.
  lamp: { 'on-dark': 'hsl(var(--shell-foreground))', 'on-light': 'hsl(var(--brand-800))' },
  brand: { 'on-dark': 'hsl(var(--brand-400))', 'on-light': 'hsl(var(--brand-400))' },
  accent: { 'on-dark': 'hsl(var(--accent-400))', 'on-light': 'hsl(var(--accent-400))' },
};

/** Lado da placa, em unidades da viewBox. Espelha o SVG oficial. */
const PLATE_SIZE = 990;
const PLATE_RADIUS = 79.31;

export type ArcusMarkProps = VariantProps<typeof arcusMarkVariants> & {
  /** `sweep` gira os anéis; `still` entrega a marca parada. */
  motion?: 'still' | 'sweep';
  /**
   * Ritmo da varredura: `'wave'` (padrão, propaga para fora) ou `'restless'`
   * (rápido e fora de fase). Aceita também um `ArcusSweep` literal — é assim que
   * o playground calibra, e não deveria aparecer em código de produto.
   */
  sweep?: ArcusSweepName | ArcusSweep;
  /**
   * Rótulo acessível. Sem ele a marca é decorativa e sai da árvore de
   * acessibilidade — que é o certo quando há texto ao lado dizendo a mesma coisa.
   */
  caps?: ArcusCaps;
  title?: string;
  className?: string;
  /** Só para dimensionar em pixel exato (playground, ícone gerado). */
  style?: CSSProperties;
};

export function ArcusMark({
  tone = 'on-dark',
  plate = 'none',
  motion = 'still',
  sweep = 'wave',
  caps = 'round',
  title,
  className,
  style,
}: ArcusMarkProps) {
  // Duas marcas na mesma página colidiriam nos ids de gradiente sem isto.
  const instanceId = useId();

  /*
   * Canvas fixo começando em `0 0`, com todos os anéis empilhados no mesmo
   * centro — e não uma viewBox simétrica em torno de `(0,0)`, que seria a
   * escrita natural da geometria.
   *
   * O motivo é um desacordo real entre navegadores: com `transform-box:
   * view-box`, o Chrome resolve `transform-origin: 50% 50%` usando o *tamanho*
   * da viewBox mas ancorando a caixa de referência em `(0,0)` do espaço de
   * usuário, ignorando um `min-x`/`min-y` negativo. Numa viewBox
   * `-376 -376 753 753` a origem calculada caía em `(376, 376)` — fora do
   * desenho — e cada anel orbitava esse ponto em vez de girar no lugar.
   *
   * Com a viewBox começando em `0 0` as duas leituras coincidem, e a origem
   * passa a ser verificável: é o centro do canvas, em unidades de usuário.
   */
  const size = plate === 'navy' ? PLATE_SIZE : ARCUS_MARK_RADIUS * 2;
  const center = { x: size / 2, y: size / 2 };
  const fillFor = (ring: ArcusRing) => RING_FILL[ring.tone][tone ?? 'on-dark'];

  /*
   * As velocidades são indexadas pela posição **entre os anéis que giram**, não
   * pela posição no desenho: a lâmpada está fora da varredura e não pode
   * consumir um degrau da escala.
   */
  const spinningRings = ARCUS_RINGS.filter((ring) => ring.spins);
  const { seconds, speeds } = resolveSweep(sweep);

  const spinStyle = (ring: ArcusRing): CSSProperties | undefined => {
    const speed = speeds[spinningRings.indexOf(ring)];

    if (motion !== 'sweep' || speed === undefined || speed <= 0) return undefined;

    return {
      // A duração é por anel: é a diferença entre elas que produz o gesto —
      // anéis travados na mesma duração leriam como logo girando inteiro.
      animationDuration: `${seconds / speed}s`,
      transformOrigin: `${center.x}px ${center.y}px`,
    };
  };

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className={cn(arcusMarkVariants({ tone, plate }), className)}
      style={style}
      // Sem rótulo a marca é ornamento: um leitor de tela anunciando "Arcus"
      // logo antes do texto "Arcus" é ruído, não informação.
      {...(title ? { role: 'img' } : { 'aria-hidden': true })}
    >
      {title ? <title>{title}</title> : null}

      {plate === 'navy' ? (
        <rect
          width={PLATE_SIZE}
          height={PLATE_SIZE}
          rx={PLATE_RADIUS}
          fill="hsl(var(--brand-800))"
        />
      ) : null}

      <g fill="none" strokeWidth={ARCUS_RING_WIDTH} strokeLinecap={caps}>
        {ARCUS_RINGS.map((ring) => {
          const ringStyle = spinStyle(ring);

          return (
            <g key={ring.radius} className={cn(ringStyle && 'arcus-mark-ring')} style={ringStyle}>
              {ring.arcs.length === 0 ? (
                <circle cx={center.x} cy={center.y} r={ring.radius} stroke={fillFor(ring)} />
              ) : (
                ring.arcs.map((arc) => {
                  const gradientId = `${instanceId}-${ring.radius}-${arc.from}`;
                  // A forma vem do módulo de geometria, que é o mesmo que
                  // `scripts/generate-brand-assets.ts` usa para escrever public/.
                  const shape = describeArcShape(ring.radius, arc, center, caps);

                  return (
                    <g key={arc.from}>
                      <defs>
                        <linearGradient
                          id={gradientId}
                          gradientUnits="userSpaceOnUse"
                          {...shape.tail.gradient}
                        >
                          <stop offset="0" stopColor={fillFor(ring)} stopOpacity="0" />
                          <stop offset="1" stopColor={fillFor(ring)} stopOpacity="1" />
                        </linearGradient>
                      </defs>

                      <path d={shape.tail.d} stroke={`url(#${gradientId})`} />

                      {shape.head ? <path d={shape.head.d} stroke={fillFor(ring)} /> : null}
                    </g>
                  );
                })
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}
